import { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from 'utils/@reduxjs/toolkit';
import { createApi } from '@reduxjs/toolkit/query/react';
import { useInjectReducer } from 'utils/redux-injectors';
import { State } from './types';
import { endpoints, baseQuery } from 'utils/api/endpoints';

export const initialState: State = {};

const slice = createSlice({
  name: 'downloadPackages',
  initialState,
  reducers: {},
});

export const api = createApi({
  reducerPath: 'downloadPackagesApi',
  baseQuery,
  tagTypes: ['DownloadPackages'],
  endpoints: builder => ({
    getPackages: builder.query<any, any>({
      query: params => ({
        url: endpoints.DOWNLOAD_PACKAGES + '/list',
        method: 'GET',
        params,
      }),
      providesTags: ['DownloadPackages'],
    }),
    addPackage: builder.mutation<any, any>({
      query: data => ({
        url: endpoints.DOWNLOAD_PACKAGES + '/create',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['DownloadPackages'],
    }),
    editPackage: builder.mutation<any, any>({
      query: ({ packageId, ...data }) => ({
        url: endpoints.DOWNLOAD_PACKAGES + '/update/' + packageId,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['DownloadPackages'],
    }),
    deletePackage: builder.mutation<any, any>({
      query: id => ({
        url: endpoints.DOWNLOAD_PACKAGES + '/delete/' + id,
        method: 'DELETE',
      }),
      invalidatesTags: ['DownloadPackages'],
    }),
    getPackage: builder.query<any, any>({
      query: id => ({
        url: endpoints.DOWNLOAD_PACKAGES + '/' + id,
        method: 'GET',
      }),
    }),
    changePackageOrder: builder.mutation<any, any>({
      query: data => ({
        url: endpoints.DOWNLOAD_PACKAGES + '/update-order',
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: ['DownloadPackages'],
    }),
    getPackageStats: builder.query<any, void>({
      query: () => ({
        url: endpoints.DOWNLOAD_PACKAGES + '/stats',
        method: 'GET',
      }),
      providesTags: ['DownloadPackages'],
    }),
    getPackageList: builder.query<any, void>({
      query: () => ({
        url: endpoints.DOWNLOAD_PACKAGES + '/all',
        method: 'GET',
      }),
    }),
    searchPackages: builder.query<any, void>({
      query: () => ({
        url: endpoints.DOWNLOAD_PACKAGES + '/search',
        method: 'GET',
      }),
    }),
    getDownloadPackagePurchasesReport: builder.query<
      any,
      { page?: number; limit?: number; search?: string }
    >({
      query: (params = {}) => ({
        url: endpoints.DOWNLOAD_PACKAGES_PURCHASES_REPORT,
        method: 'GET',
        params,
      }),
      providesTags: ['DownloadPackages'],
    }),
  }),
});

export const { actions: DownloadPackageActions } = slice;

export const useDownloadPackageSlice = () => {
  useInjectReducer({ key: slice.name, reducer: slice.reducer });
  useInjectReducer({ key: api.reducerPath, reducer: api.reducer });
  return {
    actions: DownloadPackageActions,
    useGetPackagesQuery: api.useLazyGetPackagesQuery,
    useAddPackageMutation: api.useAddPackageMutation,
    useEditPackageMutation: api.useEditPackageMutation,
    useDeletePackageMutation: api.useDeletePackageMutation,
    useGetPackageQuery: api.useLazyGetPackageQuery,
    useChangePackageOrderMutation: api.useChangePackageOrderMutation,
    useGetPackageListQuery: api.useLazyGetPackageListQuery,
    useSearchPackagesQuery: api.useLazySearchPackagesQuery,
    useGetDownloadPackagePurchasesReportQuery:
      api.useLazyGetDownloadPackagePurchasesReportQuery,
    useGetPackageStatsQuery: api.useGetPackageStatsQuery,
  };
};
